import React, { useState, useEffect } from 'react';
import { KatSpinner } from '@amzn/katal-react';
import { useCORAData } from 'src/features/hooks/useCORAData';
import { CORAInput, GetCORADataResponse, MODE_SELECTORS } from 'src/features/types/coraTypes';
import CORATable from './CORATable';
import CORANodeFailures from './CORANodeFailures';

interface CORAResultsProps {
    input?: CORAInput;
}

const CORAResults: React.FC<CORAResultsProps> = ({ input }) => {
    const [evaluationTime, setEvaluationTime] = useState<Date | null>(null);

    const { data, isLoading, error, isError } = useCORAData(input) as {
        data: GetCORADataResponse | undefined;
        isLoading: boolean;
        error: Error | null;
        isError: boolean;
    };

    useEffect(() => {
        if (data && !isLoading) {
            setEvaluationTime(new Date());
        }
    }, [data, isLoading]);

    if (!input || !input.nodeIds || input.nodeIds.length === 0) {
        return (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
                <p>Enter node ID(s) and click "Evaluate Risk" to view CORA analysis results.</p>
            </div>
        );
    }

    if (isLoading) {
        return (
            <div style={{ textAlign: 'center', padding: '2rem' }}>
                <KatSpinner size="large" />
                <p style={{ marginTop: '1rem' }}>Loading CORA risk assessment data...</p>
                <p style={{ fontSize: '0.875rem', color: '#666' }}>
                    Analyzing {input.nodeIds.length} node{input.nodeIds.length > 1 ? 's' : ''}
                    for {input.durationOfRiskWindowInDays} day{input.durationOfRiskWindowInDays > 1 ? 's' : ''}
                </p>
            </div>
        );
    }

    if (isError || error) {
        return (
            <div className="alert alert-error" style={{ margin: '1rem 0', padding: '1rem', backgroundColor: '#fee', border: '1px solid #fcc', borderRadius: '4px' }}>
                <strong>Error loading CORA data:</strong> {error?.message || 'An unexpected error occurred'}
                <br />
                <small>Please check your node IDs and try again. If the problem persists, contact support.</small>
            </div>
        );
    }

    if (!data) {
        return (
            <div className="alert alert-warning" style={{ margin: '1rem 0', padding: '1rem', backgroundColor: '#fff3cd', border: '1px solid #ffeaa7', borderRadius: '4px' }}>
                <strong>No data available:</strong> No CORA data found for the specified criteria
            </div>
        );
    }

    const hasSuccessfulNodes = data.riskDataByNode && Object.keys(data.riskDataByNode).length > 0;
    const hasFailedNodes = data.nodeFailures && Object.keys(data.nodeFailures).length > 0;

    if (!hasSuccessfulNodes && !hasFailedNodes) {
        return (
            <div className="alert alert-info" style={{ margin: '1rem 0', padding: '1rem', backgroundColor: '#d1ecf1', border: '1px solid #bee5eb', borderRadius: '4px' }}>
                No risk data found for the specified nodes and time period.
            </div>
        );
    }



    return (
        <div className="cora-results">
            {/* CORA Result Metadata Section - Between query and table */}
            <div
                className="cora-metadata-section"
                style={{
                    border: '1px solid #666',
                    borderRadius: '6px',
                    padding: '1rem',
                    margin: '0 0 0.5rem 0',
                    backgroundColor: '#f8f9fa',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
                    display: 'inline-block',
                    width: 'fit-content',
                    fontSize: '0.9rem',
                    minWidth: '320px'
                }}
            >
                <h4 style={{ margin: '0 0 0.5rem 0', color: '#333', borderBottom: '1px solid #666', paddingBottom: '0.25rem', fontSize: '1.2rem', fontWeight: 'bold' }}>
                    CORA Result Metadata
                </h4>
                <div className="cora-metadata-content">
                    <div className="metadata-row" style={{ marginBottom: '0.3rem' }}>
                        <strong>Timezone:</strong> US/Pacific
                    </div>
                    <div className="metadata-row" style={{ marginBottom: '0.3rem' }}>
                        <strong>CORA Evaluation time:</strong> {evaluationTime ? evaluationTime.toLocaleDateString('en-US', {
                            month: '2-digit',
                            day: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                            timeZone: 'America/Los_Angeles'
                        }) : 'N/A'}
                    </div>
                    <div className="metadata-row" style={{ marginBottom: '0.3rem' }}>
                        <strong>Latest Data Ingestion Time:</strong> {evaluationTime ? evaluationTime.toLocaleDateString('en-US', {
                            month: '2-digit',
                            day: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                            timeZone: 'America/Los_Angeles'
                        }) : 'N/A'}
                    </div>
                    <div className="metadata-row">
                        <strong>Utilization Features Enabled:</strong>
                        <ul style={{ margin: '0.3rem 0', paddingLeft: '1.2rem' }}>
                            {(() => {
                                const firstNodeData = Object.values(data.riskDataByNode)[0];
                                const features = firstNodeData?.featureConfigurations || [];
                                const featureDisplayNames: Record<string, string> = {
                                    'EXSD_BASED_PACK_UTILIZATION': 'Aggregate',
                                    'PROCESS_PATH_FAMILY_BASED_PACK_UTILIZATION': 'Process Path Family Awareness (S/M)'
                                };
                                return features.length > 0
                                    ? features.map((feature, idx) => (
                                        <li key={idx}>{featureDisplayNames[feature] || feature}</li>
                                    ))
                                    : <li>No features configured</li>;
                            })()}
                        </ul>
                    </div>
                </div>
            </div>

            {/* Node Failures Section */}
            {hasFailedNodes && (
                <CORANodeFailures nodeFailures={data.nodeFailures!} />
            )}

            {/* Aggregate Utilization Section - Only show when mode selector is EXSD */}
            {input?.modeSelector === MODE_SELECTORS.EXSD && (
                <div className="cora-utilization-section">
                    <h3 className="cora-utilization-title">Aggregate Utilization:</h3>

                    {/* Assumptions */}
                    <div
                        className="cora-assumptions"
                        style={{
                            border: '1px solid #ccc',
                            borderRadius: '3px',
                            padding: '0.3rem',
                            margin: '0',
                            backgroundColor: '#f9f9f9',
                            fontSize: '0.7rem',
                            display: 'inline-block',
                            width: 'fit-content'
                        }}
                    >
                        <h5 style={{ margin: '0 0 0.1rem 0', fontSize: '0.7rem', fontWeight: 'bold' }}>
                            Assumptions Made
                        </h5>
                        <p style={{ margin: '0', fontSize: '0.65rem' }}>
                            Slam Pad Time: 35 minutes
                        </p>
                    </div>

                    {/* CORA Table */}
                    <CORATable
                        data={Object.entries(data.riskDataByNode || {}).map(([nodeId, nodeData]) => ({ ...nodeData, nodeId }))}
                    />
                </div>
            )}

            {/* Singles Utilization Section - Only show when mode selector is Process Path Family */}
            {input?.modeSelector === MODE_SELECTORS.PROCESS_PATH_FAMILY && (
                <div className="singles-utilization-section">
                    <h3 className="cora-utilization-title">Singles Utilization:</h3>

                    {/* Assumptions */}
                    <div
                        className="cora-assumptions"
                        style={{
                            border: '1px solid #ccc',
                            borderRadius: '3px',
                            padding: '0.3rem',
                            margin: '0',
                            backgroundColor: '#f9f9f9',
                            fontSize: '0.7rem',
                            display: 'inline-block',
                            width: 'fit-content'
                        }}
                    >
                        <h5 style={{ margin: '0 0 0.1rem 0', fontSize: '0.7rem', fontWeight: 'bold' }}>
                            Assumptions Made
                        </h5>
                        <p style={{ margin: '0', fontSize: '0.65rem' }}>
                            Slam Pad Time: 35 minutes
                        </p>
                    </div>

                    {/* Singles CORA Table */}
                    <CORATable
                        data={Object.entries(data.riskDataByNode || {}).map(([nodeId, nodeData]) => ({ ...nodeData, nodeId }))}
                        processPathFamily="Singles"
                    />
                </div>
            )}

            {/* Multis Utilization Section - Only show when mode selector is Process Path Family */}
            {input?.modeSelector === MODE_SELECTORS.PROCESS_PATH_FAMILY && (
                <div className="multis-utilization-section" style={{ marginTop: '2rem' }}>
                    <h3 className="cora-utilization-title">Multis Utilization:</h3>

                    {/* Assumptions */}
                    <div
                        className="cora-assumptions"
                        style={{
                            border: '1px solid #ccc',
                            borderRadius: '3px',
                            padding: '0.3rem',
                            margin: '0',
                            backgroundColor: '#f9f9f9',
                            fontSize: '0.7rem',
                            display: 'inline-block',
                            width: 'fit-content'
                        }}
                    >
                        <h5 style={{ margin: '0 0 0.1rem 0', fontSize: '0.7rem', fontWeight: 'bold' }}>
                            Assumptions Made
                        </h5>
                        <p style={{ margin: '0', fontSize: '0.65rem' }}>
                            Slam Pad Time: 35 minutes
                        </p>
                    </div>

                    {/* Multis CORA Table */}
                    <CORATable
                        data={Object.entries(data.riskDataByNode || {}).map(([nodeId, nodeData]) => ({ ...nodeData, nodeId }))}
                        processPathFamily="Multis"
                    />
                </div>
            )}

        </div>
    );
};

export default CORAResults;
